import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Headset, Heart, ShieldCheck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { NAV, SITE } from "../data/content";

const CrisisBar = ({ collapsed }) => (
  <div
    className={`overflow-hidden bg-navy-deep text-offwhite transition-all duration-300 max-h-16 opacity-100 ${collapsed ? "lg:max-h-0 lg:opacity-0" : "lg:max-h-14 lg:opacity-100"}`}
    data-testid="crisis-bar"
  >
    <div className="container-x flex items-center justify-center xl:justify-between gap-4 py-2 text-xs md:text-sm">
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <span className="hidden sm:inline h-2 w-2 rounded-full bg-gold animate-pulse shrink-0" />
        <span className="font-semibold text-gold">Veteran in crisis?</span>
        <span className="text-offwhite/90">Dial <a href="tel:988" className="font-semibold underline decoration-gold/60 underline-offset-2 hover:text-gold transition-colors">988, then Press 1</a></span>
        <span className="hidden md:inline text-offwhite/70">· Text <span className="font-semibold text-offwhite">838255</span></span>
        <a href="https://www.veteranscrisisline.net/get-help-now/chat/" target="_blank" rel="noopener noreferrer" className="hidden lg:inline text-offwhite/70 hover:text-gold transition-colors">· Chat online</a>
        <span className="hidden md:inline text-offwhite/50">(24/7, confidential)</span>
      </div>
      <div className="hidden xl:flex items-center gap-5 shrink-0">
        <span className="flex items-center gap-2 text-offwhite/85">
          <Headset className="h-3.5 w-3.5 text-gold" />
          Intake Hotline: <a href={`tel:${SITE.hotline}`} className="font-semibold hover:text-gold transition-colors">{SITE.hotline}</a>
        </span>
        <Link to="/admissions#crisis" className="text-gold font-semibold hover:text-offwhite transition-colors" data-testid="crisis-quick-screener">Quick Screener</Link>
      </div>
    </div>
  </div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const navItems = NAV;
  const mobileNav = NAV.filter((n) => n.label !== "Donate");

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="site-header">
      <CrisisBar collapsed={scrolled} />
      <div
        className={`transition-all duration-300 ${
          scrolled ? "bg-offwhite/90 backdrop-blur-xl border-b border-softgray" : "bg-offwhite border-b border-softgray/60"
        }`}
      >
        <div className="container-x flex items-center justify-between gap-4 h-[72px] md:h-[84px]">
          <Link to="/" data-testid="logo-link" aria-label="Iron Shield home" className="shrink-0">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-x-4 xl:gap-x-6" data-testid="desktop-nav">
            {navItems.map((item) => {
              const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.split(" ")[0].toLowerCase()}`}
                  className={`relative text-[12px] xl:text-[13px] font-bold uppercase tracking-wide leading-tight transition-colors ${
                    active ? "text-navy" : "text-charcoal/70 hover:text-navy"
                  }`}
                >
                  {item.label}
                  {active && <span className="absolute -bottom-1.5 left-0 h-0.5 w-full bg-gold" />}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-2.5 shrink-0">
            <Link
              to="/donate"
              data-testid="header-donate-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-bold uppercase tracking-wide text-navy border border-navy rounded-md hover:bg-navy hover:text-offwhite transition-colors duration-300"
            >
              <Heart className="h-4 w-4" /> Donate
            </Link>
            <Link
              to="/admissions"
              data-testid="header-gethelp-btn"
              className="group inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-bold uppercase tracking-wide text-offwhite bg-navy rounded-md hover:bg-navy-deep transition-colors duration-300"
            >
              <ShieldCheck className="h-4 w-4" /> Get Help
            </Link>
          </div>

          <button
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 text-navy"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-drawer"
          >
            <div className="absolute inset-0 bg-charcoal/40" onClick={() => setOpen(false)} />
            <motion.div
              className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-offwhite p-6 flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-between mb-8">
                <Logo />
                <button onClick={() => setOpen(false)} className="h-11 w-11 inline-flex items-center justify-center text-navy" data-testid="mobile-menu-close" aria-label="Close menu">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex items-center gap-2.5 mb-6 rounded-sm bg-navy px-4 py-3 text-offwhite">
                <Phone className="h-4 w-4 text-gold" />
                <span className="text-sm">Crisis? <span className="font-semibold">Dial 988, press 1</span></span>
              </div>
              <nav className="flex flex-col gap-1">
                {navItems.map((item, i) => (
                  <motion.div key={item.to} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
                    <Link
                      to={item.to}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className="block py-3 font-heading text-2xl font-bold text-navy border-b border-softgray"
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="mt-auto flex flex-col gap-3 pt-8">
                <Link to="/donate" data-testid="mobile-donate-btn" className="text-center px-5 py-3.5 text-sm font-semibold text-navy border border-navy rounded-sm">
                  Donate
                </Link>
                <Link to="/admissions" data-testid="mobile-gethelp-btn" className="text-center px-5 py-3.5 text-sm font-semibold text-offwhite bg-navy rounded-sm">
                  Get Help
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
